import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input } from '@cibli/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useInterviewContext } from '@/features/common/contexts/interview.context';
import { useTapActivator } from '@/features/common/hooks/useTapActivator';
import { useBooth } from '@/features/settings/hooks/useBooth';

import { Settings } from './Settings';

const PIN_CODE: string | false = import.meta.env.VITE_PIN_CODE ?? false;
const nClicksToOpen = 6;

export default function SecretPinDialog() {
  const { setSleepMode } = useInterviewContext();
  const { isSecretPinEnabled, featureFlags } = useBooth();
  const [showPinOverlay, setShowPinOverlay] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [pin, setPin] = useState('');
  const [isWrongPin, setIsWrongPin] = useState(false);
  const pinInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const { isActivated, handleTap, reset } = useTapActivator({
    threshold: nClicksToOpen,
    timeoutMs: 1500,
  });

  const handlePinSubmit = useCallback(() => {
    const isCorrectPin = pin === PIN_CODE;
    if (isCorrectPin) {
      setShowPinOverlay(false);
      setShowSettingsDialog(true);
      setIsWrongPin(false);
    } else {
      setIsWrongPin(true);
    }
    setPin('');
  }, [pin]);

  const fullReset = useCallback(() => {
    setShowSettingsDialog(false);
    setShowPinOverlay(false);
    setIsWrongPin(false);
    setPin('');
    reset();
  }, [reset]);

  // After taps threshold reached: show PIN overlay if PIN is set, otherwise open Settings directly
  useEffect(() => {
    if (!isActivated || showPinOverlay || showSettingsDialog) return;
    setSleepMode(false);
    reset();
    if (PIN_CODE) {
      setShowPinOverlay(true);
    } else {
      setShowSettingsDialog(true);
    }
  }, [isActivated, showPinOverlay, showSettingsDialog, setSleepMode, reset]);

  // Auto-focus PIN input when overlay appears
  useEffect(() => {
    if (showPinOverlay) {
      setTimeout(() => pinInputRef.current?.focus(), 100);
    }
  }, [showPinOverlay]);

  // Reset state when PIN flag is explicitly disabled while dialog/overlay was already open
  useEffect(() => {
    if (featureFlags !== null && !isSecretPinEnabled && (showPinOverlay || showSettingsDialog)) {
      fullReset();
    }
  }, [featureFlags, fullReset, isSecretPinEnabled, showPinOverlay, showSettingsDialog]);

  // Only block after flags have loaded AND pin is explicitly disabled.
  // During loading (featureFlags === null), render permissively.
  if (featureFlags !== null && !isSecretPinEnabled) {
    return null;
  }

  return (
    <>
      {/* Invisible tap zone — bottom-left corner (48px x 256px) — only active when dialog is closed */}
      {!showPinOverlay && !showSettingsDialog && (
        <div onClick={handleTap} className="fixed left-0 bottom-0 w-12 h-64 z-1001" />
      )}

      {/* Phase 1: Standalone PIN overlay — absolute priority z-[99999] */}
      {showPinOverlay && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div
            className="bg-white rounded-lg p-6 w-80 shadow-2xl flex flex-col gap-4 border border-border"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold text-center">
              {t('pin.enter_title', { defaultValue: 'Entrer le code PIN' })}
            </h2>
            {isWrongPin && (
              <p className="text-base text-center text-red-500 font-medium">
                {t('pin.wrong', { defaultValue: 'Code PIN incorrect' })}
              </p>
            )}
            <Input
              ref={pinInputRef}
              type="password"
              autoComplete="off"
              value={pin}
              onChange={e => {
                setPin(e.target.value);
                setIsWrongPin(false);
              }}
              placeholder={t('pin.placeholder', { defaultValue: 'Entrez le code PIN' })}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handlePinSubmit();
                }
              }}
            />
            <Button onClick={handlePinSubmit}>
              {t('common.submit', { defaultValue: 'Valider' })}
            </Button>
            <Button variant="outline" onClick={fullReset}>
              {t('common.close', { defaultValue: 'Fermer' })}
            </Button>
          </div>
        </div>
      )}

      {/* Phase 2: Settings Dialog — absolute priority z-[99999] on content & overlay */}
      <Dialog
        open={showSettingsDialog}
        onOpenChange={open => {
          if (!open) {
            fullReset();
          }
        }}
      >
        <DialogContent
          className="z-[99999] overflow-y-auto overflow-x-hidden max-h-[90vh] scrollbar-hide sm:max-w-2xl"
          overlayClassName="z-[99998]"
          onOpenAutoFocus={e => {
            // Prevent Radix Dialog from automatically focusing/triggering the first interactive element (reload button)
            e.preventDefault();
          }}
        >
          <DialogHeader className="pb-3">
            <DialogTitle>Settings & Tests</DialogTitle>
          </DialogHeader>
          <Settings onClose={fullReset} />
        </DialogContent>
      </Dialog>
    </>
  );
}
